import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocale } from '@adsk/alloy-react-locale';
import { useDataApi } from '@adsk/alloy-helpers';
import {
  useCurrentAccountId,
  useCurrentProjectId,
  useCompanies,
  useRoles,
  useAlloyFeatureFlag,
  useProjectEntitlements,
} from '@adsk/alloy-provider';
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from '@adsk/alloy-react-notification';
import { withFormik } from 'formik';
import {
  ID_FIELD,
  KEY_FIELD,
  OP_STATUSES,
  SERVICE_ACCESSES,
  TRACK_EVENTS,
  FLAG_NAMES,
  filterByActiveStatus,
  mapToField,
  getMemberProductsAccessByRoles,
  getProjectProductsFromServices,
  removeExtraBlankSpaces,
} from '@adsk/acs-admin-ui-components-helpers';
import {
  useCreateAccountCompany,
  useGetAccountCompanies,
  useUserProducts,
  useAnalytics,
} from '@adsk/acs-admin-ui-components-hooks';
import {
  withAdminFFs,
  useAdminFeatureFlag,
} from '@adsk/acs-admin-ui-components-shared-components';
import { userPropType } from './propTypes';
import {
  buildGetAccountCompanyUrl,
  buildGetProjectRequest,
  buildUpdateMemberRequest,
  getProjectUserServicesAccess,
  doFetch,
  getMemberServicesFromProducts,
  getProjectUserProductsAccess,
  getMemberProductsFromServices,
} from './utils';
import { useSendAnalytics } from './hooks';
import ProjectMemberProfilePanelView from './component';

const {
  WPLT_FLAGS: { INVITE_PROJECT_MEMBER_SHOW_PRODUCTS },
  ADMIN_FLAGS: {
    ACC_REPLACE_WRITE_CONSTRUCTION_APIS,
    ENABLE_PROJECT_ROLES_PRODUCTS,
    PRODUCTS_SECTION_REDESIGN,
  },
} = FLAG_NAMES;

export const ProjectMemberProfilePanel = ({
  isEmsAccount,
  onResend,
  onRemove,
  onSaveData,
  products,
  productsLoading,
  validateUserProductAccess,
  user,
  ...rest
}) => {
  const { onClose = () => {}, values, setFieldValue } = rest;
  const {
    checkNoProductsAccess,
    companyCreateError,
    companySearchInput,
    member: {
      id: memberId,
      selectedAccessLevel,
      selectedCompany,
      selectedRoles,
      services: memberServices,
      products: memberProducts,
    },
    projectProducts,
    projectServices,
    showClosePanelConfirmation,
  } = values;
  const accountId = useCurrentAccountId();
  const projectId = useCurrentProjectId();
  const { currentProject } = useProjectEntitlements();
  const { data: roles, loading: isFetchingRoles } = useRoles();
  const { data: projectCompanies } = useCompanies();
  const { accountCompanies, onChangeCompanyInput, isFetchingAccountCompanies } =
    useGetAccountCompanies();
  const latestOnChangeCompanyInput = useRef(onChangeCompanyInput);
  const {
    loading: loadingUseWriteConstructionApis,
    value: useWriteConstructionApis,
  } = useAdminFeatureFlag(ACC_REPLACE_WRITE_CONSTRUCTION_APIS);
  const {
    loading: loadingShowProductsSectionRedesign,
    value: showProductsSectionRedesign,
  } = useAdminFeatureFlag(PRODUCTS_SECTION_REDESIGN);
  const { loading: loadingShowProducts, value: productsPermissionsActivated } =
    useAlloyFeatureFlag(INVITE_PROJECT_MEMBER_SHOW_PRODUCTS);
  const {
    loading: loadingEnableProjectRolesProducts,
    value: enableProjectRolesProducts,
  } = useAdminFeatureFlag(ENABLE_PROJECT_ROLES_PRODUCTS);
  const loadingFFs =
    loadingUseWriteConstructionApis ||
    loadingShowProductsSectionRedesign ||
    loadingShowProducts ||
    loadingEnableProjectRolesProducts;
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const { showNotification } = useNotifications();
  const { trackEvent } = useAnalytics();
  const sendAnalytics = useSendAnalytics({
    memberId: user?.id,
    selectedAccessLevel,
    selectedCompany,
    selectedRoles,
  });
  const isAccountAdmin = currentProject?.accessLevels?.accountAdministrator;
  const { userProducts, isFetchingUserOfferings } = useUserProducts(
    products,
    showProductsSectionRedesign && !isAccountAdmin
  );
  const {
    data: projectData,
    doFetch: doFetchProject,
    loading: isFetchingProjectData,
  } = useDataApi();
  const { data: accountCompanyData, doFetch: doFetchAccountCompany } =
    useDataApi();

  const projectProductIds = useMemo(
    () => mapToField(filterByActiveStatus(projectProducts), KEY_FIELD),
    [projectProducts]
  );

  const projectMemberProductIds = useMemo(
    () => mapToField(memberProducts, KEY_FIELD),
    [memberProducts]
  );

  const handleRemoveMember = (success) => {
    sendAnalytics(TRACK_EVENTS.NAME.REMOVE_MEMBER);
    onRemove(success);
  };

  const handleResendInvite = (success) => {
    sendAnalytics(TRACK_EVENTS.NAME.RESEND_INVITE);
    onResend(success);
  };

  const getSuccessCallback =
    (fieldUpdated, updatedData = {}) =>
    (updateMemberData) => {
      showNotification({
        status: NOTIFICATION_STATUSES.SUCCESS,
        message: T.translate('CHANGES_SAVED', {
          field: T.translate(`${fieldUpdated.toUpperCase()}_LABEL`),
        }),
      });

      sendAnalytics(fieldUpdated);

      const response = {
        status: OP_STATUSES.SUCCESS,
        response: useWriteConstructionApis
          ? {
              ...updateMemberData,
              ...updatedData,
            }
          : {
              ...updateMemberData,
              ...updatedData,
              products: getMemberProductsFromServices(
                updateMemberData.services,
                projectMemberProductIds
              ),
            },
      };

      if (onSaveData) onSaveData(response);
    };

  const getErrorCallback = (fieldUpdated) => (updateMemberErrorData) => {
    showNotification({
      status: NOTIFICATION_STATUSES.ERROR,
      message: T.translate('CHANGES_NOT_SAVED', {
        field: T.translate(`${fieldUpdated.toUpperCase()}_LABEL`),
      }),
    });

    const response = {
      status: OP_STATUSES.ERROR,
      response: updateMemberErrorData,
    };
    if (onSaveData) onSaveData(response);
  };

  const saveMemberData = (dataToUpdate, fieldToUpdate, updatedData = {}) => {
    const requestBody = {
      companyId: selectedCompany ? selectedCompany.id : '',
      roleIds: mapToField(selectedRoles, ID_FIELD),
      ...(useWriteConstructionApis
        ? {
            products: getProjectUserProductsAccess(
              memberProducts,
              selectedAccessLevel.value
            ),
          }
        : {
            services: getProjectUserServicesAccess(
              projectServices,
              memberServices,
              selectedAccessLevel.value
            ),
          }),
      ...dataToUpdate,
    };

    doFetch(
      ...buildUpdateMemberRequest(
        projectId,
        user.id,
        requestBody,
        useWriteConstructionApis
      ),
      getSuccessCallback(fieldToUpdate, updatedData),
      getErrorCallback(fieldToUpdate)
    );
  };

  // Handle company change
  const handleCompanyChange = (value) => {
    let newValue = value;
    if (value) {
      const companyInfo = projectCompanies
        ? projectCompanies.find(
            (projectCompany) => projectCompany.id === value.id
          )
        : null;
      newValue = {
        id: value.id,
        name: value.name,
        memberGroupId: companyInfo?.memberGroupId,
      };
    }

    setFieldValue('member.selectedCompany', newValue);
    saveMemberData({ companyId: value ? value.id : '' }, 'company', {
      companyName: value ? value.name : '',
    });
  };

  const handleCompanyInputChange = useCallback((value) => {
    setFieldValue('companySearchInput', value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle role change
  const handleRoleChange = (value) => {
    const newValue = value?.length > 0 ? value : [];

    setFieldValue('member.selectedRoles', newValue);

    const dataToUpdate = { roleIds: mapToField(newValue, ID_FIELD) };

    if (enableProjectRolesProducts) {
      const newMemberProductAccess = getMemberProductsAccessByRoles({
        currentMemberProductsAccess: memberProducts,
        previousSelectedRoles: selectedRoles,
        selectedAccessLevel: selectedAccessLevel.value,
        selectedRoles: newValue,
      });
      dataToUpdate.products = newMemberProductAccess;
      setFieldValue('member.products', newMemberProductAccess);
      const hasAdminAccess = newMemberProductAccess.some(
        (memberProductAccess) => {
          return memberProductAccess.access === SERVICE_ACCESSES.ADMIN;
        }
      );
      if (hasAdminAccess) {
        setFieldValue('member.selectedAccessLevel', {
          value: SERVICE_ACCESSES.ADMIN,
        });
      }
    }

    saveMemberData(dataToUpdate, 'role');
  };

  const handlePanelClose = () => {
    if (checkNoProductsAccess && validateUserProductAccess) {
      setFieldValue('showClosePanelConfirmation', true);
    } else {
      sendAnalytics(TRACK_EVENTS.NAME.CLOSE);
      onClose();
    }
  };

  const handleConfirmPanelClose = () => {
    setFieldValue('showClosePanelConfirmation', false);
    sendAnalytics(TRACK_EVENTS.NAME.CLOSE);
    onClose();
  };

  const handleCancelPanelClose = () => {
    setFieldValue('showClosePanelConfirmation', false);
  };

  // company creation
  const onAccountCompanyCreation = (companyId, newCompany) => {
    handleCompanyChange(newCompany);
  };

  const { createAccountCompanyRequest } = useCreateAccountCompany({
    onSuccess: onAccountCompanyCreation,
    onError: () => setFieldValue('companyCreateError', true),
  });

  const handleCreateCompany = () => {
    createAccountCompanyRequest(removeExtraBlankSpaces(companySearchInput));
    trackEvent(
      `${TRACK_EVENTS.SCOPE.PROJECT_ADMIN}.${TRACK_EVENTS.NAME.COMPANY}.${TRACK_EVENTS.NAME.CREATE}`,
      { origin: 'member_profile' }
    );
  };

  useEffect(() => {
    latestOnChangeCompanyInput.current = onChangeCompanyInput;
  }, [onChangeCompanyInput]);

  useEffect(() => {
    const handler = setTimeout(() => {
      latestOnChangeCompanyInput.current(
        removeExtraBlankSpaces(companySearchInput)
      );
    }, 800);

    return () => clearTimeout(handler);
  }, [companySearchInput]);

  // Fetch project services when projectId changes
  useEffect(() => {
    if (projectId === undefined || useWriteConstructionApis === undefined)
      return;

    doFetchProject(buildGetProjectRequest(projectId, useWriteConstructionApis));
  }, [projectId, doFetchProject, useWriteConstructionApis]);

  useEffect(() => {
    if (!projectData || loadingUseWriteConstructionApis) return;

    if (useWriteConstructionApis) {
      setFieldValue('projectProducts', projectData.products);
    } else {
      setFieldValue('projectServices', projectData.services);
      setFieldValue(
        'projectProducts',
        getProjectProductsFromServices(projectData.services)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingUseWriteConstructionApis, projectData, useWriteConstructionApis]);

  // Set user editable values based on passed user prop
  useEffect(() => {
    if (!user || !projectProductIds) return;

    let selectedCompanyInfo;
    let selectedRolesInfo;
    if (user.companyId) {
      const companyInfo = projectCompanies.find(
        (projectCompany) => projectCompany.id === user.companyId
      );
      if (companyInfo) {
        selectedCompanyInfo = {
          id: user.companyId,
          name: companyInfo.name,
          memberGroupId: companyInfo.memberGroupId,
        };
      } else {
        selectedCompanyInfo = { id: user.companyId };
      }
    }
    if (user.roleIds) {
      selectedRolesInfo = roles.filter((role) =>
        user.roleIds.some((memberRole) => role.id === memberRole)
      );
    }

    setFieldValue('member', {
      autodeskId: user.autodeskId,
      id: user.id,
      selectedAccessLevel: {
        value: user.accessLevels.projectAdmin
          ? SERVICE_ACCESSES.ADMIN
          : SERVICE_ACCESSES.MEMBER,
      },
      selectedCompany: selectedCompanyInfo,
      selectedRoles: selectedRolesInfo,
      services: user.services
        ? user.services
        : getMemberServicesFromProducts(user.products),
      products: !user.services
        ? user.products
        : getMemberProductsFromServices(user.services, projectProductIds),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectProductIds, user]);

  // Get the member's initial company name
  useEffect(() => {
    if (!accountId || !user?.companyId) return;

    doFetchAccountCompany(
      buildGetAccountCompanyUrl(accountId, user?.companyId)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, user?.companyId]);

  /** If the member's initial company is not in the first 200 companies,
   * store the name in the state, otherwise the SelectBox will be empty */
  useEffect(() => {
    if (
      selectedCompany?.id &&
      !selectedCompany?.name &&
      accountCompanyData &&
      selectedCompany?.id === accountCompanyData?.id
    ) {
      setFieldValue('member.selectedCompany', accountCompanyData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, accountCompanyData]);

  if (!user) return null;

  return (
    <ProjectMemberProfilePanelView
      {...rest}
      companies={accountCompanies}
      companyCreateError={companyCreateError}
      companySearchInput={companySearchInput}
      isEmsAccount={isEmsAccount}
      isFetchingCompanies={isFetchingAccountCompanies}
      isFetchingRoles={isFetchingRoles}
      isLoadingProductsSection={
        loadingFFs ||
        productsLoading ||
        isFetchingUserOfferings ||
        isFetchingProjectData ||
        !memberId
      }
      onCancelPanelClose={handleCancelPanelClose}
      onChangeCompanyInput={handleCompanyInputChange}
      onClose={handlePanelClose}
      onConfirmPanelClose={handleConfirmPanelClose}
      onCompanyChange={handleCompanyChange}
      onCreateCompany={handleCreateCompany}
      onRemove={handleRemoveMember}
      onResend={handleResendInvite}
      onRoleChange={handleRoleChange}
      onSaveData={onSaveData}
      products={userProducts}
      roles={roles}
      saveMemberData={saveMemberData}
      selectedCompany={selectedCompany}
      selectedRoles={selectedRoles}
      showClosePanelConfirmation={showClosePanelConfirmation}
      showProductsSection={productsPermissionsActivated}
      showProductsSectionRedesign={!!showProductsSectionRedesign}
      user={user}
      useWriteConstructionApis={useWriteConstructionApis}
    />
  );
};

ProjectMemberProfilePanel.propTypes = {
  /**
   * Handle UI if the user account is EMS (or CPA)
   */
  isEmsAccount: PropTypes.bool,
  /** Callback executed when the user clicks on the Remove member option.<br>
   * A boolean argument will be passed to the callback to indicate if the request was successful.
   */
  onRemove: PropTypes.func.isRequired,
  /** Callback executed when the user clicks on the Resend invitation option.<br>
   * A boolean argument will be passed to the callback to indicate if the request was successful.
   */
  onResend: PropTypes.func.isRequired,
  /** callback with information on the save operation  */
  onSaveData: PropTypes.func,
  /** Products list */
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  productsLoading: PropTypes.bool,
  /** Project member user data to display */
  user: userPropType,
  validateUserProductAccess: PropTypes.bool,
};

ProjectMemberProfilePanel.defaultProps = {
  isEmsAccount: false,
  onSaveData: undefined,
  productsLoading: false,
  user: undefined,
  validateUserProductAccess: true,
};

const ProjectMemberProfilePanelWithFormik = withFormik({
  mapPropsToValues: () => ({
    accessLevelChangeConfirmationProps: {},
    checkNoProductsAccess: false,
    companyCreateError: false,
    companySearchInput: '',
    member: {
      autodeskId: null,
      id: null,
      selectedAccessLevel: null,
      selectedCompany: null,
      selectedRoles: [],
      services: null,
      products: null,
    },
    projectProducts: [],
    projectServices: [],
    showClosePanelConfirmation: false,
  }),
  displayName: 'ProjectMemberProfilePanel',
})(ProjectMemberProfilePanel);

export default withAdminFFs(ProjectMemberProfilePanelWithFormik);
